/**
 * Precision Privacy (2026-08-31): ONE app. The privacy program is the shell;
 * the impermissible use and breach lifecycle (the existing Privacy Command
 * Center) is one module inside it and is rendered as it is, untouched.
 * Layout, header and tab strip follow the FDR Oversight shell exactly.
 */
import React, { useCallback, useEffect, useState } from 'react';
import IncidentsModule from './IncidentsModule';
import ProgramHome from './ProgramHome';
import MapView from './MapView';
import VendorsView from './VendorsView';
import RightsView from './RightsView';
import DiscoveryView from './DiscoveryView';
import BoardReport from './BoardReport';
import { api } from './api';

const TABS = [
  { id: 'home', label: 'Program', tip: 'Where the privacy program stands: what waits for you, what is confirmed, how the map gets built.', icon: 'M3 12l9-8 9 8M5 10v10h14V10' },
  { id: 'map', label: 'Where information moves', tip: 'Every flow of member information: from where, to where, what, why, who may see it, how long it is kept. Each line carries its source.', icon: 'M4 6h16M4 12h16M4 18h16' },
  { id: 'vendors', label: 'Vendors and business associates', tip: 'Every vendor from FDR Oversight that may hold member information: what binds them, their breach notification deadline, what they do and do not do.', icon: 'M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6' },
  { id: 'rights', label: 'Member rights and notices', tip: 'The Notice of Privacy Practices members receive, and every request a member makes about their own information, answered on the regulation\'s clock.', icon: 'M9 12h6m-6 4h6M9 8h6M5 4h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1z' },
  { id: 'discovery', label: 'Questionnaires and interviews', tip: 'Ask each department lead nine plain questions, then hold a short interview. What they say goes on the map with their name and the date.', icon: 'M8 10h8M8 14h5M21 12a8 8 0 01-11.6 7.1L4 20l1-4.6A8 8 0 1121 12z' },
  { id: 'incidents', label: 'Impermissible uses and breaches', tip: 'The incident lifecycle: intake, assessment, breach determination, notifications, mitigation, closure. Work plan, dashboard, search and law library.', icon: 'M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z' },
];

function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-flex" onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && text && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 w-72 px-4 py-3 bg-slate-800 text-white text-xs leading-relaxed rounded-xl shadow-2xl pointer-events-none">
          {text}
          <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2.5 h-2.5 bg-slate-800 rotate-45" />
        </div>
      )}
    </div>
  );
}

export default function PrivacyProgram({ user, onSelectApp }) {
  const [view, setView] = useState('home');
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  // A case opened from the map lands on its page inside the incident module.
  const [incidentToOpen, setIncidentToOpen] = useState(null);
  const openIncident = (id) => { setIncidentToOpen(id); setView('incidents'); };

  const loadOverview = useCallback(async () => {
    try { const d = await api('/overview'); if (d.success) setOverview(d); } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  const attention = (overview?.flows?.draft || 0) + ((overview?.vendors?.vendors || 0) - (overview?.vendors?.with_baa || 0));

  return (
    <div className="h-full flex flex-col bg-[#f8fafc] min-w-0 min-h-0">
      <header className="flex-shrink-0 bg-white border-b border-slate-200/60">
        <div className="px-8 pt-6 pb-4">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-orange-500/25 ring-2 ring-white/20">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800 tracking-tight">Precision Privacy</h1>
              <p className="text-slate-400 text-sm mt-0.5">Where member information lives and moves, who may touch it, what binds the vendors who hold it, and how impermissible uses and breaches are handled</p>
            </div>
          </div>
        </div>
        <div className="px-8 pb-0">
          <div className="flex gap-0.5 overflow-x-auto scrollbar-hide">
            {TABS.map((tab) => (
              <Tooltip key={tab.id} text={tab.tip}>
                <button type="button" onClick={() => setView(tab.id)}
                  className={`relative flex items-center gap-1.5 px-4 py-3 text-sm font-semibold transition-all rounded-t-xl whitespace-nowrap ${
                    view === tab.id ? 'bg-[#f8fafc] text-orange-700 border border-slate-200/60 border-b-[#f8fafc] -mb-px z-10' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} /></svg>
                  {tab.label}
                  {tab.id === 'home' && attention > 0 && <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center">{attention}</span>}
                </button>
              </Tooltip>
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1 min-h-0 overflow-hidden flex">
        {view === 'incidents' ? (
          <div className="flex-1 min-w-0 min-h-0 overflow-hidden"><IncidentsModule initialIncidentId={incidentToOpen} onConsumedInitial={() => setIncidentToOpen(null)} /></div>
        ) : (
          <div className="flex-1 min-w-0 min-h-0 overflow-y-auto">
            <div className="px-8 py-6 max-w-[1500px]">
              {view === 'home' && <ProgramHome overview={overview} loading={loading} go={setView} refresh={loadOverview} />}
              {view === 'report' && <BoardReport onBack={() => setView('home')} refreshOverview={loadOverview} />}
              {view === 'map' && <MapView overview={overview} refreshOverview={loadOverview} openIncident={openIncident} />}
              {view === 'vendors' && <VendorsView />}
              {view === 'rights' && <RightsView refreshOverview={loadOverview} />}
              {view === 'discovery' && <DiscoveryView user={user} refreshOverview={loadOverview} />}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
